import { useMemo } from "react";

// Simple EPG guide with fixed 6-hour window starting at current hour
export default function ChannelGuide({ channels, activeSlug, onSelect }) {
  const timeline = useMemo(() => {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    const slots = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(start.getTime() + i * 60 * 60 * 1000);
      slots.push(d);
    }
    return { start, slots };
  }, []);

  const SLOT_WIDTH = 200; // px per hour
  const totalWidth = SLOT_WIDTH * timeline.slots.length;

  const nowOffsetPx = (() => {
    const now = new Date();
    const diffMin = (now - timeline.start) / 60000;
    return (diffMin / 60) * SLOT_WIDTH;
  })();

  return (
    <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl overflow-hidden" data-testid="channel-guide-epg">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div>
          <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.2em] uppercase">On Air Now</p>
          <h3 className="font-heading text-2xl text-white mt-1">Channel Guide</h3>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-white/50">
          <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
          <span>Currently airing</span>
        </div>
      </div>

      <div className="flex">
        {/* Left: channel list */}
        <div className="w-56 flex-shrink-0 border-r border-white/10 bg-[#050507]/60">
          <div className="h-10 border-b border-white/10 flex items-center px-4">
            <span className="text-[10px] text-white/40 uppercase tracking-widest">Channel</span>
          </div>
          {channels.map((ch) => (
            <button
              key={ch.id}
              onClick={() => onSelect(ch.slug)}
              data-testid={`epg-channel-${ch.slug}`}
              className={`w-full flex items-center gap-3 px-4 py-4 border-b border-white/5 text-left transition-colors hover:bg-white/5 ${activeSlug === ch.slug ? "bg-[#D4AF37]/10" : ""}`}
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex-shrink-0 border border-white/10">
                <img src={ch.logo_url} alt={ch.name} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <p className="font-heading text-white font-semibold truncate">{ch.name}</p>
                <p className="text-[11px] text-white/50 truncate">{ch.tagline}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Right: timeline */}
        <div className="flex-1 overflow-x-auto row-scroll">
          <div style={{ width: totalWidth, minWidth: totalWidth }} className="relative">
            {/* Time header */}
            <div className="h-10 border-b border-white/10 flex sticky top-0 bg-[#0A0A0E] z-10">
              {timeline.slots.map((d, i) => (
                <div key={i} style={{ width: SLOT_WIDTH }} className="border-r border-white/5 flex items-center px-3">
                  <span className="text-xs text-white/60 font-medium tracking-wide">
                    {d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>

            {/* Rows */}
            {channels.map((ch) => (
              <ChannelRow key={ch.id} channel={ch} timelineStart={timeline.start} slotWidth={SLOT_WIDTH} totalWidth={totalWidth} />
            ))}

            {/* Now indicator */}
            {nowOffsetPx > 0 && nowOffsetPx < totalWidth && (
              <div className="absolute top-10 bottom-0 w-px bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.7)] z-20 pointer-events-none" style={{ left: nowOffsetPx }}>
                <div className="w-3 h-3 -ml-[5px] -mt-[6px] rounded-full bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.9)]" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChannelRow({ channel, timelineStart, slotWidth, totalWidth }) {
  const now = new Date();
  return (
    <div className="relative h-20 border-b border-white/5">
      {channel.schedule.map((p) => {
        const start = new Date(p.start_time);
        const end = new Date(start.getTime() + p.duration_min * 60000);
        const offsetMin = (start - timelineStart) / 60000;
        const left = (offsetMin / 60) * slotWidth;
        const width = (p.duration_min / 60) * slotWidth;
        if (left + width < 0 || left > totalWidth) return null;
        const isNow = now >= start && now < end;
        return (
          <div
            key={p.id}
            data-testid={`epg-program-${p.id}`}
            className={`epg-block absolute top-2 bottom-2 px-3 py-2 rounded-lg border cursor-pointer overflow-hidden bg-white/5 border-white/10 ${isNow ? "now" : ""}`}
            style={{ left: Math.max(0, left), width: Math.min(width, totalWidth - Math.max(0, left)) }}
          >
            <div className="flex items-start justify-between gap-2 h-full">
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm text-white font-semibold truncate">{p.title}</p>
                <p className="text-[11px] text-white/50 truncate">{p.host || p.description}</p>
              </div>
              {isNow && <span className="text-[9px] font-bold tracking-widest text-[#D4AF37]">NOW</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
